import { StatusCodes } from "http-status-codes";
import {
  IClaimRes,
  IClaimSubmissionResponse,
} from "../../../interfaces/IPreAuthClaim";
import Axios from "../../../utils/axiosInterceptor";
import { PATIENT_APIS } from "../../../utils/constants";

export const fetchPortalDataEchs = async (caseId: number, visitId: number) => {
  try {
    const res = await Axios.get(
      `${PATIENT_APIS.ECHS_SERVICE}/cases/${caseId}/visitInfo/${visitId}/getAllCasePreAuthByCaseIdAndVisitId`
    );
    return res.data;
  } catch (error) {
    console.error("Failed to get portalData:", error);
    throw new Error("Failed to get portal data");
  }
};

export const saveDraftEchs = async (
  caseId: number,
  visitId: number,
  payload: any
) => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.ECHS_SERVICE}/cases/${caseId}/visitInfo/${visitId}/casePreAuth`,
      payload
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot save Pre Auth details:", error?.response?.data);
    throw new Error(error?.response?.data || "Cannot save Pre Auth details");
  }
};

export const updateDraftEchs = async (
  caseId: number,
  visitId: number,
  preAuthId: number,
  payload: any
) => {
  try {
    const response = await Axios.patch(
      `${PATIENT_APIS.ECHS_SERVICE}/cases/${caseId}/visitInfo/${visitId}/casePreAuthId/${preAuthId}`,
      payload
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot update Pre Auth details:", error?.response?.data);
    throw new Error(error?.response?.data || "Cannot update Pre Auth details");
  }
};

export const fetchPreAuthToPortalEchs = async (
  caseId: number,
  visitId: number,
  payload: any
) => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.PREAUTH_DATA}/api/echs/cases/${caseId}/visitInfo/${visitId}/initiateCasePreAuth`,
      payload
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot post Pre Auth details:", error?.response?.data);
    throw new Error(error?.response?.data || "Cannot post Pre Auth details");
  }
};

export const getPreAuthClaimECHS = async (caseId: number, visitId: number) => {
  try {
    const response = await Axios.get(
      `${PATIENT_APIS.ECHS_SERVICE}/casesNew/${caseId}/visitInfo/${visitId}/caseClaimSubmission`
    );

    if (response.status === StatusCodes.OK) {
      return { claimSubmissionRes: response.data, status: response.status };
    } else {
      return {
        errorMessage: "Cannot get claim details",
        status: response.status,
      };
    }
  } catch (error: any) {
    console.error("Cannot get claim details:", error);
    throw new Error(
      error?.response?.data?.message || "Please, Try again later"
    );
  }
};

export const intimateClaimEchs = async (
  data: IClaimRes,
  caseId: number,
  visitId: number,
  portalId: number,
  caseClaimId: number | null
): Promise<IClaimSubmissionResponse> => {
  try {
    let response;
    const baseUrl = `${PATIENT_APIS.ECHS_SERVICE}/casesNew/${caseId}/visitInfo/${visitId}/portalMetaData/${portalId}/caseClaimIntimate`;
    response = await Axios.post(`${baseUrl}`, data);
    if (response.status === StatusCodes.OK) {
      return { claimSubmissionRes: response.data, status: response.status };
    } else {
      return {
        errorMessage: response?.data,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Cannot intimate claim details:", error);
    throw new Error("Cannot intimate claim details");
  }
};

export const deleteProcedureEchs = async (portalId: number, data: any) => {
  try {
    const response = await Axios.put(
      `${PATIENT_APIS.ECHS_DELETE_PROCEDURE}/${portalId}/casePreAuthCGHS_Procedure`,
      data
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const postPreAuthClaimToEdgeProEchs = async (
  data: IClaimRes,
  caseId: number,
  visitId: number,
  portalId: number,
  caseClaimId: number | null
): Promise<IClaimSubmissionResponse> => {
  try {
    let response;
    const baseUrl = `${PATIENT_APIS.ECHS_SERVICE}/casesNew/${caseId}/visitInfo/${visitId}/portalMetaData`;
    if (caseClaimId === null) {
      // POST request
      response = await Axios.post(
        `${baseUrl}/${portalId}/createCaseClaim`,
        data
      );
    } else {
      response = await Axios.patch(
        `${baseUrl}/${portalId}/caseClaim/${caseClaimId}/createCaseClaim`,
        data
      );
    }
    if (response.status === StatusCodes.OK) {
      return { claimSubmissionRes: response.data, status: response.status };
    } else {
      return {
        errorMessage: response?.data,
        status: response.status,
      };
    }
  } catch (error) {
    console.error("Cannot post claim details:", error);
    throw new Error("Cannot post claim details");
  }
};

export const postPreAuthClaimEchs = async (
  data: IClaimRes,
  caseId: number,
  visitId: number,
  portalId: number
): Promise<IClaimSubmissionResponse> => {
  try {
    const response = await Axios.post(
      `${PATIENT_APIS.ECHS_SERVICE}/casesNew/${caseId}/visitInfo/${visitId}/portalMetaData/${portalId}/caseClaimSubmission`,
      data
    );
    if (response.status === StatusCodes.OK) {
      return { claimSubmissionRes: response.data, status: response.status };
    } else {
      return {
        errorMessage: response?.data,
        status: response.status,
      };
    }
  } catch (error: any) {
    console.error("Cannot post claim details:", error?.response?.data);
    throw new Error(error?.response?.data || "Cannot post claim details");
  }
};

export const submitApprovalEchs = async (formData: any) => {
  try {
    const res = await Axios.post(
      `${PATIENT_APIS.APPROVE_PREAUTH}/echs/approve`,
      formData
    );
    return res.data;
  } catch (error) {
    throw new Error("Failed to Approve");
  }
};

export const dischargeServiceEchs = {
  getDischargeData: async (
    caseId: number | undefined,
    visitId: number | undefined
  ) => {
    try {
      const response = await Axios.get(
        `${PATIENT_APIS.ECHS_SERVICE}/casesNew/${caseId}/visitInfo/${visitId}/caseDischarges`
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching discharge data:", error);
      throw error;
    }
  },

  saveDischargeData: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalId: number | undefined,
    caseDischargeId: number | null,
    payload: any
  ): Promise<void> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.ECHS_SERVICE}/casesNew/${caseId}/visitInfo/${visitInfoId}/portalMetaData`;

      if (caseDischargeId === null) {
        // POST request
        response = await Axios.post(
          `${baseUrl}/${portalId}/caseDischarges`,
          payload
        );
      } else {
        // PATCH request
        response = await Axios.patch(
          `${baseUrl}/${portalId}/caseDischarge/${caseDischargeId}/caseDischarges`,
          payload
        );
      }

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to save discharge data");
      }
    } catch (error) {
      console.error("Error saving discharge data:", error);
      throw error;
    }
  },
  saveDischargeDataInEdgePro: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalId: number | undefined,
    caseDischargeId: number | null,
    payload: any
  ): Promise<any> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.ECHS_SERVICE}/casesNew/${caseId}/visitInfo/${visitInfoId}/portalMetaData`;
      // POST request
      if (caseDischargeId === null) {
        response = await Axios.post(
          `${baseUrl}/${portalId}/createCaseDischarge`,
          payload
        );
      } else {
        response = await Axios.patch(
          `${baseUrl}/${portalId}/caseDischarge/${caseDischargeId}/createCaseDischarge`,
          payload
        );
      }

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to save enhancement data");
      }

      return response;
    } catch (error) {
      console.error("Error saving enhancement data:", error);
      throw error;
    }
  },

  saveIntimateDischarge: async (
    caseId: number | undefined,
    visitInfoId: number | undefined,
    portalMetaDataId: number | undefined,
    payload: any
  ): Promise<any> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.ECHS_SERVICE}/casesNew/${caseId}/visitInfo/${visitInfoId}/portalMetaData/${portalMetaDataId}/createCaseDischargeIntimation`;
      // POST request

      response = await Axios.post(`${baseUrl}`, payload);

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to save intimate discharge data");
      }
      return response;
    } catch (error) {
      console.error("Error saving intimate discharge data:", error);
      // throw error;
      throw new Error("Failed to save intimate discharge data");
    }
  },
  updateClaimId: async (
    formData: any,
    caseId: number | undefined,
    visitId: number | undefined,
    schemeId: number | undefined
  ): Promise<any> => {
    try {
      let response;
      const baseUrl = `${PATIENT_APIS.GET_PRE_AUTH_QUERY_DATA}/${caseId}/visitInfo/${visitId}/schemeId/${schemeId}`;

      response = await Axios.patch(`${baseUrl}`, formData);

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Failed to update claim Id ");
      }
      return response;
    } catch (error) {
      console.error("Failed to update claim Id:", error);
      // throw error;
      throw new Error("Failed to update claim Id");
    }
  },
};

export const getConsultation = async () => {
  try {
    const res = await Axios.get(
      `${PATIENT_APIS.ECHS_CLAIM_API}/echs-consultation`
    );
    return res.data;
  } catch (error) {
    throw new Error("Failed to get consultation");
  }
};

export const getRoomCharges = async () => {
  try {
    const res = await Axios.get(
      `${PATIENT_APIS.ECHS_CLAIM_API}/echs-room-charges`
    );
    return res.data;
  } catch (error) {
    throw new Error("Failed to room charges");
  }
};

export const getImplants = async (hospitalId: any, schemeId: any) => {
  try {
    const response = await Axios.post(PATIENT_APIS.ECHS_IMPLANTS, {
      hospitalId, schemeId
    });
    return response.data;
  } catch (error: any) {
    console.error("Cannot get implants:", error);
    throw new Error(
      error?.response?.data?.message ||
        "Please, Try again later failed to get implants"
    );
  }
};

export const getIcdCodes = async (data: any) => {
  try {
    const response = await Axios.post(PATIENT_APIS.ECHS_ICD_CODES, data);
    return response.data;
  } catch (error: any) {
    console.error("Cannot get icd codes:", error);
    throw new Error(
      error?.response?.data?.message ||
        "Please, Try again later failed to get icd codes"
    );
  }
};

export const getPolyclinic = async (hospitalId: any, schemeId: any) => {
  try {
    const response = await Axios.post(`${PATIENT_APIS.ECHS_SERVICE}/polyclinic`, {
      hospitalId,
      schemeId,
    });
    return response.data;
  } catch (error: any) {
    console.error("Cannot get polyclinic list:", error);
    throw new Error(
      error?.response?.data?.message ||
        "Please, Try again later failed to get polyclinic list"
    );
  }
};

export const getDigitallySignedDocument = async (payload: any) => {
  try {
    const res = await Axios.post(`${PATIENT_APIS.ECHS_SERVICE}/check-digital-signatures`, payload);
    return res.data;
  }
  catch(error: any) {
    console.error("Cannot get digitally signed document list:", error);
    throw new Error(
      error?.response?.data?.message ||
        "Please, Try again later failed to get digitally signed document list"
    );
  }
}
